/**
 * The config snapshots, field by field: one row per dotted path, one column
 * per experiment. "Only show differences" is on by default. Long values
 * (prompts) fold away, and the lines that are not in the reference's value
 * are marked, so a one-line prompt edit is easy to spot.
 */

import { Option } from "@/components/ui/field";
import { SeriesLabel } from "./series-label";
import type { Comparison } from "./types";

const LONG = 140;

function text(value: unknown): string {
  if (value === null || value === undefined) return "—";
  return typeof value === "string" ? value : JSON.stringify(value);
}

function Value({ value, against, isRef }: { value: unknown; against: unknown; isRef: boolean }) {
  const shown = text(value);
  const refLines = new Set(text(against).split("\n"));
  const lines = shown.split("\n").map((line, i) => (
    <span
      key={i}
      className={`block rounded-[3px] px-1 whitespace-pre-wrap ${!isRef && !refLines.has(line) ? "bg-run-bg text-diff-ink" : ""}`}
    >
      {line || " "}
    </span>
  ));
  if (shown.length <= LONG && lines.length === 1) {
    return <div className="font-mono text-xs break-all">{lines}</div>;
  }
  return (
    <details className="font-mono text-xs">
      <summary className="cursor-pointer text-body">
        {shown.split("\n")[0]?.slice(0, 60)}…{" "}
        <span className="text-muted">({shown.split("\n").length} lines)</span>
      </summary>
      <div className="mt-1.5 break-words">{lines}</div>
    </details>
  );
}

export function CompareConfig({
  data,
  refId,
  onlyDiff,
  onToggle,
}: {
  data: Comparison;
  refId: string;
  onlyDiff: boolean;
  onToggle: (onlyDiff: boolean) => void;
}) {
  const all = data.config_diff;
  const differing = all.filter((r) => r.differs).length;
  const rows = onlyDiff ? all.filter((r) => r.differs) : all;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2.5">
        <Option type="checkbox" checked={onlyDiff} onChange={(e) => onToggle(e.target.checked)}>
          Only show differences
        </Option>
        <span className="grow" />
        <span className="text-xs text-muted">Snapshots compared field by field, so any connector works.</span>
      </div>
      <section className="box" aria-label="Config differences">
        <div className="overflow-x-auto">
          <table className="tbl table-fixed">
            <thead>
              <tr>
                <th className="w-[220px]">Field</th>
                {data.experiments.map((e, i) => (
                  <th key={e.id}>
                    <SeriesLabel experiment={e} index={i} isRef={e.id === refId} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.path} className={r.differs ? "bg-changed-bg" : ""}>
                  <td className="align-top font-mono text-xs break-all text-body">{r.path}</td>
                  {data.experiments.map((e) => (
                    <td key={e.id} className="align-top">
                      <Value value={r.values[e.id]} against={r.values[refId]} isRef={e.id === refId} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-line-soft bg-well px-4 py-2.5 text-xs text-muted">
          {differing} of {all.length} fields differ{onlyDiff ? " · unchanged fields hidden" : ""}
        </div>
      </section>
    </>
  );
}
